import {settlePayrollCreation} from '../../api/payroll';
import { preloadCreationEditor } from './recordEditorLoader';
import { beginForegroundRead } from '../../components/interaction/readiness';
import { registerCreationWriteGuard } from '../../api/dataSync';
import { settleRecordCreation } from '../../api/erp';
import { settleSupplierProductCreation, settleSupplierCreation } from '../../api/supplier';

export type CreationResource = 'payroll'|'live'|'purchase'|'inventory'|'products'|'suppliers'|'members'|'warehouses'|'complaintTypes'|'attributes'|'attributeOptions'|'categories';
const paths:Record<CreationResource,string[]> = {payroll:['/payroll/records/'],categories:['/product-categories/'],live:['/livestreams/'],purchase:['/procurements/'],inventory:['/inventory/','/product/masters/','/product-masters/'],products:['/supplier/products/'],suppliers:['/supplier/suppliers/'],members:['/streamers/'],warehouses:['/procurement-warehouses/'],complaintTypes:['/complaint-types/'],attributes:['/product-attributes/'],attributeOptions:['/product-attributes/']};
const creations = new Map<string,{resource:CreationResource;id:number;token:string;retaining?:Promise<void>}>();
const finishing = new Map<string,Promise<boolean>>();
const pending = new Map<string,Promise<void>>();
const keyOf = (resource:CreationResource,id:number) => `${resource}:${id}`;
function settle(resource:CreationResource,id:number,token:string,retain=false) {
    if(resource==='payroll') return settlePayrollCreation(id,token,retain);
    if(resource==='suppliers') return settleSupplierCreation(id,token,retain);
    return resource==='products' ? settleSupplierProductCreation(id,token,retain) : settleRecordCreation(resource,id,token,retain);
}
export function registerCreation(resource:CreationResource,id:number,token?:string) {
    if(token && !creations.has(keyOf(resource,id))) creations.set(keyOf(resource,id),{resource,id,token});
}
/** Every entry registers the receipt before exposing its created record to an editor. */
export async function createRegisteredRecord<T extends {_creation_token?:string}>(
    resource:CreationResource, create:()=>Promise<T>, identify:(record:T)=>number,
):Promise<T> {
    const finish = beginForegroundRead({});
    void preloadCreationEditor(resource).catch(() => {});
    try {
        const record=await create();
        registerCreation(resource,identify(record),record._creation_token);
        return record;
    } finally { finish(); }
}
/** Close and record switching use the same save barrier and untouched-record decision. */
export function finishRecordCreation(resource:CreationResource,id:number,save?:()=>Promise<unknown>):Promise<boolean> {
    const key=keyOf(resource,id),active=finishing.get(key);
    if(active)return active;
    const result=(async()=>{
        if(save && await save()===false)return false;
        await discardUntouchedCreation(resource,id);
        return true;
    })();
    finishing.set(key,result);
    void result.finally(()=>{finishing.delete(key);}).catch(()=>{});
    return result;
}
export async function retainCreation(resource:CreationResource,id:number):Promise<void> {
    const key=keyOf(resource,id), creation=creations.get(key);
    if(!creation)return;
    if(!creation.retaining)creation.retaining=settle(resource,id,creation.token,true).then(()=>{creations.delete(key);});
    try{await creation.retaining;}catch(error){creation.retaining=undefined;throw error;}
}
// Persist a sticky receipt before the first write. Even a later clear/revert or
// failed save must never turn an edited record back into an automatically removable record.
registerCreationWriteGuard(url=>{
    if(/\/(?:discard-empty(?:-draft)?|retain-creation(?:-draft)?)(?:\?|$)/.test(url))return;
    const path=url.split('?')[0];
    const targets=[...creations.values()].filter(creation=>{
        if(creation.resource==='inventory'&&path.includes('/supplier/'))return false;
        if(creation.resource==='attributeOptions')return path.endsWith('/options/'+creation.id);
        return paths[creation.resource].some(prefix=>path.includes(`${prefix}${creation.id}/`)||path.endsWith(`${prefix}${creation.id}`));
    });
    if(targets.length)return Promise.all(targets.map(creation=>retainCreation(creation.resource,creation.id))).then(()=>{});
});

export function discardUntouchedCreation(resource:CreationResource,id:number):Promise<void> {
    const key=keyOf(resource,id), active=pending.get(key), creation=creations.get(key);
    if(active) return active;
    if(!creation) return Promise.resolve();
    if(creation.retaining) return creation.retaining;
    const result=settle(resource,id,creation.token).then(()=>{creations.delete(key);});
    pending.set(key,result);
    void result.finally(()=>{pending.delete(key);}).catch(()=>{});
    return result;
}
